import React, { ReactNode, useCallback, useRef, useState } from 'react'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Skeleton from 'components_new/atoms/Skeleton'
import Text, { TextProps } from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import LabelItem from 'components_new/molecules/LabelItem'

import { formatValue } from 'components_new/organisms/Widget/Types/utils'
import {
  AUTO_SIGNIFICANT_FIGURES,
  getAutoScale,
  TUnitPrefixOption
} from 'utils/autoFormat'
import { ClickAwayListener, Popper, useTheme } from '@mui/material'

export interface ValueTextProps extends Omit<TextProps, 'children'> {
  displayPositiveIndicator?: boolean
  error?: string
  label?: string
  loading?: boolean
  popperItem?: ReactNode
  significantFigures?: number
  tooltip?: ReactNode
  unit?: '%' | null | string
  // lets the value pick a larger unit, e.g. "41,2 mkr"
  unitPrefixes?: TUnitPrefixOption[]
  value: number | null
}

const ValueText = React.forwardRef((props: ValueTextProps, ref: any) => {
  const {
    className,
    displayPositiveIndicator,
    error,
    loading,
    onClick,
    popperItem,
    significantFigures = AUTO_SIGNIFICANT_FIGURES,
    sx,
    tooltip,
    unit,
    unitPrefixes,
    value,
    ...rest
  } = props

  const { t } = useTranslation()
  const anchorRef = useRef<HTMLElement | null>(null)

  const [clickOpen, setClickOpen] = useState<boolean>(false)

  const setTextRef = useCallback(
    (node: HTMLElement | null) => {
      anchorRef.current = node

      if (typeof ref === 'function') ref(node)
      else if (ref) (ref as { current: HTMLElement | null }).current = node
    },
    [ref]
  )

  // loading
  let content = <Skeleton variant="text" width={48} />

  if (error) {
    // error
    content = (
      <>
        <Icon
          name={'Error'}
          sx={{ fontSize: '0.8rem', verticalAlign: 'middle' }}
        />{' '}
        {t('global', 'error')}
      </>
    )
  } else if (!loading) {
    // success
    const { unitPrefix, unitLabel } = getAutoScale([value], unit, unitPrefixes)

    content = (
      <>
        {displayPositiveIndicator && value !== null && value > 0 ? '+' : ''}
        {formatValue(value, unitPrefix, 1, true, undefined, significantFigures)}
        {value === null || !unitLabel
          ? null
          : unitLabel === '%'
            ? unitLabel
            : ' ' + unitLabel}
      </>
    )
  }

  return (
    <LabelWrapper {...props} ref={ref}>
      <PopperWrapper
        anchorEl={anchorRef.current}
        onClose={() => setClickOpen(false)}
        open={clickOpen}
        popperItem={popperItem}
      >
        <Tooltip placement={error ? 'top' : undefined} title={error ?? tooltip}>
          <Text
            className={className}
            color={value === null ? 'text.disabled' : undefined}
            component="div"
            ref={setTextRef}
            noWrap={true}
            onClick={
              onClick
                ? (event) => onClick(event)
                : popperItem
                  ? () => setClickOpen(true)
                  : undefined
            }
            sx={{
              fontVariantNumeric: 'tabular-nums',
              ...(onClick || popperItem || tooltip
                ? {
                    cursor: tooltip ? 'help' : 'pointer',
                    textDecorationColor: 'rgba(26, 26, 29, 0.38)', // text.disabled
                    textDecorationLine: 'underline',
                    textDecorationStyle: 'dotted'
                  }
                : null),
              ...sx,
              ...(error
                ? {
                    color: 'warning.text',
                    cursor: 'not-allowed'
                  }
                : null)
            }}
            variant="navigation"
            {...rest}
          >
            {content}
          </Text>
        </Tooltip>
      </PopperWrapper>
    </LabelWrapper>
  )
})

interface LabelWrapperProps extends ValueTextProps {
  children: ReactNode
}

const LabelWrapper = React.forwardRef((props: LabelWrapperProps, ref: any) => {
  const { align, children, loading, label, sx } = props

  if (label)
    return (
      <LabelItem
        align={align}
        label={label}
        loading={loading}
        ref={ref}
        sx={sx}
      >
        {children}
      </LabelItem>
    )

  return <>{children}</>
})

interface PopperWrapperProps {
  anchorEl: HTMLElement | null
  children: ReactNode
  onClose: () => void
  open: boolean
  popperItem?: ReactNode
}

const PopperWrapper = (props: PopperWrapperProps) => {
  const { anchorEl, children, onClose, open, popperItem } = props

  const theme = useTheme()

  if (popperItem)
    return (
      <>
        {children}

        <Popper
          anchorEl={anchorEl}
          modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
          open={open}
          placement="bottom"
          style={{ zIndex: theme.zIndex.tooltip }}
        >
          <ClickAwayListener onClickAway={onClose}>
            <Box
              sx={{
                borderRadius: 1,
                boxShadow: theme.shadows[20],
                maxWidth: 480
              }}
            >
              {popperItem}
            </Box>
          </ClickAwayListener>
        </Popper>
      </>
    )

  return <>{children}</>
}

ValueText.displayName = 'ValueText'

export default ValueText
